/**
 * Data and privacy.
 *
 * This screen exists so a merchant can answer a request from one of *their*
 * visitors without emailing us. Under GDPR they are the controller for what
 * their shoppers type into the widget; we are the processor. A compliance
 * feature that requires a support ticket is one that happens late, or never.
 *
 * The tone here is deliberately plain. Every control on this page destroys
 * something, and a screen that is coy about that is how people delete a year
 * of transcripts by accident.
 */

import { useState } from "react";

import {
  useEraseVisitor,
  useExportData,
  useRetention,
  useSetRetention,
} from "../api/queries";
import { useCurrentOrganization } from "../auth/session";
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
} from "../ui/primitives";

/** Offered as buttons because a free-text box invites "1". */
const PRESETS = [30, 90, 180, 365, 730];

export function PrivacyPage() {
  const organization = useCurrentOrganization();
  const orgId = organization?.organization.id ?? "";

  const retention = useRetention(orgId);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold">Data and privacy</h1>
        <p className="text-sm text-muted">
          What we keep, for how long, and how to remove it.
        </p>
      </div>

      {retention.isLoading ? (
        <Loading />
      ) : retention.error ? (
        <ErrorNote error={retention.error} />
      ) : (
        <RetentionCard orgId={orgId} />
      )}

      <ExportCard orgId={orgId} />
      <ErasureCard orgId={orgId} />
    </div>
  );
}

function RetentionCard({ orgId }: { orgId: string }) {
  const retention = useRetention(orgId);
  const save = useSetRetention(orgId);
  const data = retention.data;

  if (!data) return null;

  const current = data.effective_retention_days;

  return (
    <Card title="How long transcripts are kept">
      <p className="text-sm text-muted">
        Conversations older than this are deleted automatically, along with
        every message in them. Usage and billing records are not affected.
      </p>

      {save.error != null && (
        <div className="mt-3">
          <ErrorNote error={save.error} />
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {PRESETS.map((days) => (
          <Button
            key={days}
            size="sm"
            variant={days === current ? "primary" : "secondary"}
            aria-pressed={days === current}
            disabled={save.isPending}
            onClick={() => save.mutate(days)}
          >
            {days >= 365 ? `${days / 365} year${days > 365 ? "s" : ""}` : `${days} days`}
          </Button>
        ))}
      </div>

      <p className="mt-3 text-sm">
        Currently keeping transcripts for <strong>{current} days</strong>
        {data.conversation_retention_days === null && (
          // Saying so matters: a merchant who has never opened this screen
          // still has a policy, and it moves when the platform default does.
          <> — the platform default, which you have not changed.</>
        )}
        .
      </p>

      {data.conversation_retention_days !== null && (
        <Button
          className="mt-3"
          size="sm"
          variant="ghost"
          disabled={save.isPending}
          onClick={() => save.mutate(null)}
        >
          Follow the platform default ({data.platform_default_days} days)
        </Button>
      )}

      <p className="mt-4 text-xs text-muted">
        Shortening this does not delete anything straight away — the next
        cleanup does, usually within a few hours. There is no option to keep
        transcripts forever: storage limitation is a legal requirement, not a
        preference.
      </p>
    </Card>
  );
}

function ExportCard({ orgId }: { orgId: string }) {
  const exportData = useExportData(orgId);

  return (
    <Card title="Download everything">
      <p className="text-sm text-muted">
        Your organisation, sites, indexed content, every transcript and your
        usage totals, as a newline-delimited JSON file. One record per line, so
        it opens in anything.
      </p>

      {exportData.error != null && (
        <div className="mt-3">
          <ErrorNote error={exportData.error} />
        </div>
      )}

      <Button
        className="mt-3"
        variant="primary"
        disabled={exportData.isPending}
        onClick={() => exportData.mutate()}
      >
        {exportData.isPending ? "Preparing…" : "Download my data"}
      </Button>

      <p className="mt-3 text-xs text-muted">
        A large catalogue can take a minute. Only owners can download an export.
      </p>
    </Card>
  );
}

function ErasureCard({ orgId }: { orgId: string }) {
  const [visitorId, setVisitorId] = useState("");
  const [confirming, setConfirming] = useState(false);
  const erase = useEraseVisitor(orgId);

  const receipt = erase.data;

  return (
    <Card title="Erase a visitor's conversations">
      <p className="text-sm text-muted">
        If a shopper asks you to delete what they typed into your assistant,
        this removes it. Find their visitor ID on the conversation in{" "}
        <strong>Conversations</strong>.
      </p>

      {erase.error != null && (
        <div className="mt-3">
          <ErrorNote error={erase.error} />
        </div>
      )}

      {receipt != null && (
        // A receipt, not a toast: demonstrating compliance later means being
        // able to say what was removed, and this is the number to write down.
        <p role="status" className="mt-3 rounded-lg bg-surface p-3 text-sm">
          Deleted {receipt.conversations_deleted} conversation
          {receipt.conversations_deleted === 1 ? "" : "s"} and{" "}
          {receipt.messages_deleted} message
          {receipt.messages_deleted === 1 ? "" : "s"}.
        </p>
      )}

      <form
        className="mt-4 flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (visitorId.trim()) setConfirming(true);
        }}
      >
        <Field label="Visitor ID" id="visitor-id">
          <Input
            id="visitor-id"
            value={visitorId}
            placeholder="v_8f3a…"
            onChange={(event) => setVisitorId(event.target.value)}
          />
        </Field>
        <Button type="submit" disabled={!visitorId.trim() || erase.isPending}>
          Erase
        </Button>
      </form>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Erase this visitor's conversations?"
      >
        <p className="text-sm">
          Every conversation this visitor had with your assistant will be
          deleted, along with the messages inside them.{" "}
          <strong>This cannot be undone.</strong>
        </p>
        <p className="mt-2 text-sm text-muted">
          Your usage and billing records are not affected — they hold no
          message text.
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button
            variant="danger"
            disabled={erase.isPending}
            onClick={() => {
              erase.mutate(visitorId.trim(), {
                onSuccess: () => {
                  setConfirming(false);
                  setVisitorId("");
                },
              });
            }}
          >
            {erase.isPending ? "Erasing…" : "Erase permanently"}
          </Button>
        </div>
      </Modal>
    </Card>
  );
}
